import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Card, CardHeader } from '@/components/ui/Card'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn } from '@/lib/cn'

/* KPI tile: an eyebrow-ish label, a display value with tabular numerals, and
 * one line of delta or context. Tiles NEVER use a coloured background — the
 * number carries the colour. No shadow. */

export interface KpiTileProps {
  label: string
  /** Pre-formatted figure, e.g. "412 800". Format with lib/format. */
  value: string
  /** Trails the figure in a muted weight, e.g. "USD". */
  unit?: string
  delta?: {
    label: string
    tone: 'positive' | 'negative' | 'attention' | 'neutral'
  }
  link?: { to: string; label: string }
  /** Chart, bar, or any visual sitting under the value. */
  children?: ReactNode
  /** Breakdown rows in the tile's footer. */
  footer?: ReactNode
  className?: string
}

export function KpiTile({
  label,
  value,
  unit,
  delta,
  link,
  children,
  footer,
  className,
}: KpiTileProps) {
  return (
    <Card className={cn('h-full', className)}>
      <CardHeader
        action={
          link ? (
            <Link to={link.to} className="text-[12px] font-normal">
              {link.label}
            </Link>
          ) : null
        }
      >
        <span className="text-text-muted text-[13px] leading-tight font-normal">
          {label}
        </span>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <span className="asa-num text-text-heading text-[26px] leading-none font-semibold tracking-[-0.02em] whitespace-nowrap">
            {value}
            {unit ? (
              <span className="text-text-muted ml-1 text-[13px] font-normal">
                {unit}
              </span>
            ) : null}
          </span>
          {delta ? (
            <StatusPill
              tone={delta.tone === 'neutral' ? 'neutral' : delta.tone}
              dot={false}
              className="h-[19px] px-[7px] py-0"
            >
              {delta.label}
            </StatusPill>
          ) : null}
        </div>
      </CardHeader>

      {children}

      {footer ? (
        <div className="border-neutral-100 mt-2.5 flex flex-col border-t px-[18px] pt-1.5 pb-3.5">
          {footer}
        </div>
      ) : null}
    </Card>
  )
}

/* ── Breakdown rows ───────────────────────────────────────────────────────── */

export interface BreakdownRowProps {
  icon?: ReactNode
  label: string
  value: string
  /** Right-most column: a signed change, a share, or nothing. */
  trailing?: ReactNode
  tone?: 'default' | 'positive' | 'negative' | 'attention'
  last?: boolean
}

export function BreakdownRow({
  icon,
  label,
  value,
  trailing,
  tone = 'default',
  last = false,
}: BreakdownRowProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 py-[9px] text-[13px] leading-[1.3]',
        !last && 'border-neutral-100 border-b',
      )}
    >
      {icon ? <span className="flex shrink-0 items-center">{icon}</span> : null}
      <span className="text-text-body min-w-0 truncate">{label}</span>
      <span className="flex-1" />
      <span
        className={cn(
          'asa-num whitespace-nowrap',
          tone === 'negative'
            ? 'text-negative font-semibold'
            : tone === 'positive'
              ? 'text-positive'
              : tone === 'attention'
                ? 'text-attention'
                : 'text-text-heading',
        )}
      >
        {value}
      </span>
      <span className="w-12 shrink-0 text-right">{trailing}</span>
    </div>
  )
}

/** The signed change that trails a breakdown row. Carries an arrow as well as
 *  a colour, because colour never carries meaning alone. */
export function DeltaText({ percent }: { percent: string }) {
  const n = Number(percent)
  if (!Number.isFinite(n)) return null

  const positive = n >= 0
  return (
    <span
      className={cn(
        'asa-num text-[12px] leading-none font-medium',
        positive ? 'text-positive' : 'text-negative',
      )}
    >
      {positive ? '↑' : '↓'} {positive ? '+' : '−'}
      {Math.abs(n).toFixed(1)}%
    </span>
  )
}

/** A muted share-of-total figure, e.g. "74%". */
export function ShareText({ percent }: { percent: string }) {
  const n = Number(percent)
  if (!Number.isFinite(n)) return null
  return (
    <span className="asa-num text-text-muted text-[12px] leading-none font-normal">
      {Math.round(n)}%
    </span>
  )
}
